<template>
  <Modal v-model="isVisible" class="queue-not-started-modal">
    <template #title>Action Required: No one has been offered your job yet</template>
    <template #content>
      <p>
        The people available for your job will not receive a job offer unless you complete your job
        offer queue.
      </p>
      <p>
        Select those who are a good fit by checking the box next to their name, then click
        <strong>Build Job Offer Queue</strong>. From there, put them in your preferred order and set
        a response time. Only one person is offered the job at a time.
      </p>
      <p>(You are also able to come back to this step in the future.)</p>
      <p>Do you want to continue to build your Job Offer Queue, and send a job offer?</p>
      <div class="gap-16 flex-col">
        <BaseButton class="button-primary mx-auto" @click="cancelNavigation">
          Yes, take me back to complete my Job Offer Queue.
        </BaseButton>
        <BaseButton class="button-inline mx-auto" @click="continueNavigation">
          No, I don’t want to find help for this job right now.
        </BaseButton>
      </div>
    </template>
  </Modal>
</template>

<script>
export default {
  name: 'QueueNotStartedModal',
  model: {
    event: 'update:callback',
    prop: 'callback',
  },
  props: {
    /** @type {import('vue/types/options').PropOptions<import('vue-router').NavigationGuardNext | null | undefined>} */
    callback: Function,
  },
  data() {
    return {
      /** @type {import('vue-router').NavigationGuardNext | null} */
      next: null,
    };
  },
  computed: {
    isVisible: {
      /**
       * @returns {boolean}
       */
      get() {
        return typeof this.next === 'function';
      },
      /**
       * @param {boolean} value
       * @returns {void}
       */
      set(value) {
        if (!value) {
          this.cancelNavigation();
        }
      },
    },
    /**
     * @returns {JobQueue | null}
     */
    queue() {
      return this.$store.getters['events/searchQueue'];
    },
  },
  methods: {
    /**
     * @returns {void}
     */
    cancelNavigation() {
      return this.resolveNavigation(false);
    },
    /**
     * @returns {void}
     */
    continueNavigation() {
      return this.resolveNavigation();
    },
    /**
     * @param {Parameters<import('vue-router').NavigationGuardNext>} args
     * @returns {void}
     */
    resolveNavigation(...args) {
      const { next } = this;
      this.next = null;
      if (typeof next === 'function') {
        return next(...args);
      }
    },
  },
  watch: {
    /**
     * @param {import('vue-router').NavigationGuardNext | null | undefined} next
     * @returns {void}
     */
    next(next) {
      this.$emit('update:callback', next);
    },
    callback: {
      /**
       * @param {import('vue-router').NavigationGuardNext | null | undefined} next
       * @returns {void}
       */
      handler(next) {
        // If a previous navigation attempt is still pending, then cancel it.
        const oldNext = this.next;
        if (typeof oldNext === 'function' && oldNext !== next) {
          oldNext(false);
        }

        // If the new callback isn't a function, then nothing needs to be done.
        if (typeof next !== 'function') {
          this.next = null;
          return;
        }

        // Check the status of the job queue.
        switch (this.queue?.status) {
          case 'created':
          case 'matchingFailed':
          case 'paused':
            /*
             * If the queue status is one of these values, then the queue is inactive. In this
             * scenario, the navigation request needs to be suspended until the user confirms
             * whether they want to allow the queue to remain inactive by leaving the current
             * page.
             */
            this.next = next;
            break;
          default:
            /*
             * Any other status value indicates that either
             * 1. the queue is already running, or
             * 2. the queue was never successfully loaded.
             *
             * In either case, the navigation attempt is resolved immediately.
             */
            this.next = null;
            next();
            break;
        }
      },
      immediate: true,
    },
  },
};
</script>
